import { useEffect } from 'react';

/** Pure: set `target.scrollTop` to the same scroll fraction as `source`. */
export function syncScroll(source: HTMLElement, target: HTMLElement): void {
  const srcMax = source.scrollHeight - source.clientHeight;
  const fraction = srcMax > 0 ? source.scrollTop / srcMax : 0;
  const tgtMax = target.scrollHeight - target.clientHeight;
  target.scrollTop = fraction * tgtMax;
}

/**
 * Two-way proportional scroll sync between two scrollers, active only when
 * `enabled`. A short re-entrancy lock stops the programmatic scroll from
 * echoing back and fighting the user. Takes the elements directly (not refs)
 * so the effect re-runs when a lazily-mounted scroller appears.
 */
export function useScrollSync(
  a: HTMLElement | null,
  b: HTMLElement | null,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled || !a || !b) return;

    let locked = false;
    const driver = (from: HTMLElement, to: HTMLElement) => () => {
      if (locked) return;
      locked = true;
      syncScroll(from, to);
      requestAnimationFrame(() => {
        locked = false;
      });
    };
    const onA = driver(a, b);
    const onB = driver(b, a);
    a.addEventListener('scroll', onA, { passive: true });
    b.addEventListener('scroll', onB, { passive: true });
    return () => {
      a.removeEventListener('scroll', onA);
      b.removeEventListener('scroll', onB);
    };
  }, [a, b, enabled]);
}
